<template>
  <div class="flex flex-col space-y-4">
    <div class="flex items-center justify-between">
      <!--tabbar-->
      <slot />
      <!--search panel-->
      <div class="flex items-center gap-2">
        <div class="relative">
          <el-input
            placeholder="Search..."
            prefix-icon="el-icon-search"
            class="w-[250px]"
            clearable
            v-model="searchText"
          />
        </div>
        <el-button @click="onEditProduct()" icon="el-icon-plus" type="primary">
          Add Product
        </el-button>
        <el-popover
          placement="bottom"
          trigger="click"
          width="320"
          v-model="filterPanelVisible"
        >
          <button
            slot="reference"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 w-10"
            type="button"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="radix-_r_1t_"
            data-state="closed"
          >
            <i class="icon-funnel h-4 w-4" />
          </button>
          <ETagFilterPanel @apply="handleApply" />
        </el-popover>
      </div>
    </div>
    <div class="flex flex-col flex-1 outline-none space-y-6 mt-6">
      <div
        class="flex flex-col flex-1 bg-card text-card-foreground gap-6 rounded-xl border py-6 shadow-sm"
      >
        <div
          class="@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6"
        >
          <div class="leading-none font-semibold">Products</div>
          <div class="text-muted-foreground text-sm">
            Manage your product catalog and pricing information.
          </div>
        </div>
        <div class="flex flex-col flex-1 px-6">
          <div
            data-slot="table-container"
            class="relative w-full overflow-x-auto"
          >
            <table
              v-loading="tableCtrl.loading"
              data-slot="table"
              class="w-full caption-bottom text-sm flex-1"
            >
              <thead data-slot="table-header" class="[&_tr]:border-b">
                <tr
                  data-slot="table-row"
                  class="hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors"
                >
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Image
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Product Name
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Barcode
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Price(#pp)
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Discount Price(#vp)
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Product Storage
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                  >
                    Category
                  </th>
                  <th
                    data-slot="table-head"
                    class="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody data-slot="table-body" class="[&_tr:last-child]:border-0">
                <!-- skeleton screen -->
                <tr v-if="isLoading">
                  <td colspan="8" class="px-4 py-10">
                    <div class="flex flex-col gap-2">
                      <div
                        v-for="n in 5"
                        :key="n"
                        class="flex items-center gap-4 animate-pulse"
                      >
                        <div
                          class="w-12 h-12 flex-shrink-0 bg-gray-200 rounded-md"
                        ></div>
                        <div class="flex-1">
                          <div class="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                          <div class="h-3 bg-gray-100 rounded w-1/4"></div>
                        </div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/6"></div>
                      </div>
                    </div>
                  </td>
                </tr>
                <!-- data rows -->
                <tr
                  v-else
                  v-for="product in products"
                  :key="getProductKey(product)"
                  data-slot="table-row"
                  class="hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors"
                >
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    <div class="w-12 h-12 bg-muted rounded-md overflow-hidden">
                      <SmartImage
                        :src="getProductImage(product)"
                        :alt="getProductName(product)"
                        class="w-full h-full object-cover"
                      />
                    </div>
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    <div>
                      <div class="font-medium">
                        {{ getProductName(product) }}
                      </div>
                      <div class="text-sm text-muted-foreground">
                        {{ product.ps || product.pg || "No description" }}
                      </div>
                    </div>
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    {{ getBarcode(product) }}
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    {{ formatPrice(product.pp) }}
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap text-green-600"
                  >
                    {{ formatPrice(product.vp) }}
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    {{ getInventoryCount(product) }}
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap"
                  >
                    <span
                      data-slot="badge"
                      class="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground"
                    >
                      {{ getProductCategory(product) }}
                    </span>
                  </td>
                  <td
                    data-slot="table-cell"
                    class="p-2 align-middle whitespace-nowrap text-right"
                  >
                    <el-popover
                      placement="bottom-end"
                      trigger="click"
                      :visible.sync="popoverVisible[getProductKey(product)]"
                      popper-class="min-w-[8rem] p-1"
                    >
                      <template #reference>
                        <button
                          slot="reference"
                          data-slot="dropdown-menu-trigger"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all size-9 hover:bg-accent hover:text-accent-foreground"
                          type="button"
                          @click.stop="togglePopover(getProductKey(product))"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-ellipsis-vertical h-4 w-4"
                          >
                            <circle cx="12" cy="12" r="1"></circle>
                            <circle cx="12" cy="5" r="1"></circle>
                            <circle cx="12" cy="19" r="1"></circle>
                          </svg>
                        </button>
                      </template>
                      <div class="flex flex-col min-w-[8rem]">
                        <div
                          role="menuitem"
                          data-slot="dropdown-menu-item"
                          data-variant="default"
                          class="focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none"
                          data-orientation="vertical"
                          @click="onEditProduct(product)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-square-pen h-4 w-4 mr-2"
                          >
                            <path
                              d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            ></path>
                            <path
                              d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"
                            ></path>
                          </svg>
                          Edit Product
                        </div>
                        <div
                          role="separator"
                          aria-orientation="horizontal"
                          data-slot="dropdown-menu-separator"
                          class="bg-gray-500 -mx-1 my-1 h-px"
                        ></div>
                        <div
                          role="menuitem"
                          data-slot="dropdown-menu-item"
                          data-variant="default"
                          class="focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none text-red-600"
                          data-orientation="vertical"
                          @click="handleDeleteProduct(product)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-trash h-4 w-4 mr-2"
                          >
                            <path d="M3 6h18"></path>
                            <path
                              d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"
                            ></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                          Delete
                        </div>
                      </div>
                    </el-popover>
                  </td>
                </tr>
                <tr v-if="!isLoading && !products.length">
                  <td
                    class="px-4 py-10 text-center text-muted-foreground"
                    colspan="8"
                  >
                    No products found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          v-if="tableCtrl"
          class="px-6 pb-6 pt-4 border-t border-border flex justify-end"
        >
          <el-pagination
            @size-change="(val) => tableCtrl.setPageSize(val)"
            @current-change="(val) => tableCtrl.setCurrentPage(val)"
            :current-page="tableCtrl.state.pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="tableCtrl.state.pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableCtrl.state.pagination.total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import { getEslProducts, bulkDeleteEslProducts, downloadFile } from "@/api";
import ETagFilterPanel from "./ETagFilterPanel.vue";
import SmartImage from "@/components/SmartImage.vue";

export default {
  name: "ProductsList",
  props: {
    searchText: {
      type: String,
      default: "",
    },
    queryParams: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    ETagFilterPanel,
    SmartImage,
  },
  data() {
    return {
      tableCtrl: null,
      popoverVisible: {},
      filterPanelVisible: false,
    };
  },
  computed: {
    products() {
      const rows = this.tableCtrl?.state.tableData ?? [];
      return this.applySearchFilter(rows);
    },
    isLoading() {
      return this.tableCtrl?.state.loading ?? false;
    },
  },
  watch: {
    searchText() {
      if (!this.tableCtrl) {
        return;
      }
      this.tableCtrl.state.pagination.page = this.tableCtrl.pageBase;
      this.syncQueryParameters();
    },
    queryParams: {
      deep: true,
      handler() {
        this.syncQueryParameters();
      },
    },
  },
  created() {
    this.initTableController();
  },
  methods: {
    initTableController() {
      const initialQuery = this.buildQueryParameters();
      this.tableCtrl = new TableController({
        loadMethod: this.fetchProducts,
        pageSize: 15,
        isClientPaged: false,
        keyField: "pc",
        pageField: "f1",
        pageSizeField: "f2",
        pageBase: 1,
        totalField: "totalCount",
        queryParameters: initialQuery,
      });
    },
    togglePopover(key) {
      this.$set(this.popoverVisible, key, !this.popoverVisible[key]);
    },
    closePopover(key) {
      this.$set(this.popoverVisible, key, false);
    },
    async onEditProduct(product) {
      const key = this.getProductKey(product);
      this.closePopover(key);
      // Wait a tick to ensure popover is hidden
      await this.$nextTick();
      await this.$loaderWindow({
        title: `${product ? "Edit" : "Create"} Product`,
        component: () => import("./EditProduct.vue"),
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          productCode: product?.pc,
        },
      });
      this.$message.success(`Product has been saved successfully.`);
      this.reloadProducts();
    },
    async fetchProducts(params = {}) {
      try {
        const response = await getEslProducts(params);
        const list = this.normalizeProducts(this.extractProductList(response));
        const totalCount = this.extractTotalCount(response, list.length);
        return { list, totalCount };
      } catch (error) {
        console.error("Failed to load ESL products", error);
        return { list: [], totalCount: 0 };
      }
    },
    buildQueryParameters() {
      const rawParams = {
        ...(this.queryParams || {}),
      };
      const keyword = (this.searchText || "").trim();
      if (keyword) {
        rawParams.keyword = keyword;
      } else {
        delete rawParams.keyword;
      }
      return Object.keys(rawParams).reduce((acc, key) => {
        const value = rawParams[key];
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});
    },
    syncQueryParameters() {
      if (!this.tableCtrl) {
        return;
      }
      this.tableCtrl.setQueryParameters(this.buildQueryParameters());
    },
    extractProductList(response) {
      if (Array.isArray(response)) {
        return response;
      }
      if (Array.isArray(response?.list)) {
        return response.list;
      }
      if (Array.isArray(response?.data?.list)) {
        return response.data.list;
      }
      return [];
    },
    extractTotalCount(response, fallbackLength = 0) {
      if (typeof response?.totalCount === "number") {
        return response.totalCount;
      }
      if (typeof response?.total === "number") {
        return response.total;
      }
      if (typeof response?.data?.totalCount === "number") {
        return response.data.totalCount;
      }
      if (typeof response?.data?.total === "number") {
        return response.data.total;
      }
      return fallbackLength;
    },
    normalizeProducts(list = []) {
      return list.map((item, index) => {
        const productCode = item.pc || item.product_code || `product-${index}`;
        return {
          ...item,
          pc: productCode,
          pn: item.pn || item.product_name,
          ps: item.ps || item.product_spec || item.product_desc,
          pg: item.pg || item.product_grade,
          pp: item.pp ?? item.product_price ?? item.product_sales_price,
          vp: item.vp ?? item.product_vip_price ?? item.product_sales_price,
          pu: item.pu || item.product_unit,
          pim: item.pim || item.product_image,
          barcode: item.barcode || item.product_inner || productCode,
          inventory:
            item.inventory ??
            item.product_storage ??
            item.stock ??
            item.quantity ??
            item.qty,
          category: item.category || item.product_grade || item.pg,
        };
      });
    },
    reloadProducts() {
      if (this.tableCtrl) {
        this.tableCtrl.loadTableData();
      }
    },
    applySearchFilter(list = []) {
      const search = (this.searchText || "").trim().toLowerCase();
      if (!search) {
        return list;
      }
      return list.filter((item = {}) => {
        const values = [
          item.pc,
          item.product_code,
          item.pn,
          item.product_name,
          item.ps,
          item.product_desc,
          item.pg,
          item.product_grade,
        ];
        return values.some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(search)
        );
      });
    },
    getProductKey(product) {
      return product?.pc || product?.product_code || product?.pn;
    },
    getProductName(product = {}) {
      return product.pn || product.product_name || "Unnamed product";
    },
    getProductImage(product = {}) {
      const img = product.product_image || product.pim;
      if (img) {
        if (/^https:/.test(img)) {
          return img;
        }
        // 特殊处理 http://oa.greendisplay.cn:8099/upload/images/000/product_image/pri653b5551b9bd7.png?089237113cf7dec68b445fcd740ab8214ccb8ff3
        else if (img.startsWith("http://oa.greendisplay.cn")) {
          const imageName = img.split("/").pop().split("?")[0];
          return downloadFile(
            `${process.env.VUE_APP_API_URL_BASE}/fission/e-tag/upload/images/000/product_image/${imageName}`
          );
        } else if (img.startsWith("/")) {
          // return `${process.env.VUE_APP_E_TAG_SERVER_URL_BASE}${img}`;
          const imageName = img.split("/").pop().split("?")[0];
          return downloadFile(
            `${process.env.VUE_APP_API_URL_BASE}/fission/e-tag/${imageName}`
          );
        }
      }
      return "/placeholder.svg?height=100&width=100";
    },
    getBarcode(product = {}) {
      return (
        product.barcode ||
        product.product_inner ||
        product.pc ||
        product.product_code ||
        "--"
      );
    },
    getInventoryCount(product = {}) {
      const value = product.product_storage;
      if (value === undefined || value === null || value === "") {
        return "--";
      }
      return value;
    },
    getProductCategory(product = {}) {
      return (
        product.category ||
        product.category_name ||
        product.product_grade ||
        product.pg ||
        "Uncategorized"
      );
    },
    formatPrice(value) {
      if (value === undefined || value === null || value === "") {
        return "--";
      }
      const number = Number(value);
      if (Number.isFinite(number)) {
        return `$${number.toFixed(2)}`;
      }
      return value;
    },
    async handleDeleteProduct(product) {
      this.closePopover(this.getProductKey(product));
      await this.$confirm(
        `Are you sure you want to delete the product "${product.pn}"? This action cannot be undone.`,
        {
          title: "Delete Product",
          confirmButtonText: "Delete",
        }
      );
      await bulkDeleteEslProducts({ f1: [product.pc] });
      this.$message.success(`Product has been deleted successfully.`);
      this.reloadProducts();
    },
    handleApply() {
      this.filterPanelVisible = false;
      this.reloadProducts();
    },
  },
};
</script>
